import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatCLP, formatRut, validateRut, apiError, formatDateTime } from "../../lib/format";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle, Receipt } from "@phosphor-icons/react";

const BANKS = ["Banco de Chile", "BancoEstado", "Banco Santander", "BCI", "Scotiabank", "Itaú", "Banco Falabella", "MercadoPago", "TenPo", "MACH", "Otro"];

export default function Transfers() {
  const { user, refreshMe } = useAuth();
  const [step, setStep] = useState(1);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    bank: "Banco de Chile", account_type: "Cuenta Corriente", account_number: "",
    recipient_name: "", recipient_rut: "", recipient_email: "", amount: "", comment: "",
  });
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me/transfers").then((r) => {
      setHistory(r.data);
      // If there's a pending-token transfer, jump straight to step 4 with it
      const pending = r.data.find((t) => t.status === "pending_token");
      if (pending) {
        setReceipt(pending);
        setStep(4);
      }
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goNext = () => {
    if (step === 1) {
      if (!form.bank || !form.account_type || !form.account_number) return toast.error("Completa los datos bancarios");
      if (!form.recipient_name || form.recipient_name.trim().length < 3) return toast.error("Nombre del destinatario requerido");
      if (!validateRut(form.recipient_rut)) return toast.error("RUT del destinatario inválido");
      if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.recipient_email)) return toast.error("Correo del destinatario inválido");
    }
    if (step === 2) {
      const amt = Number(form.amount);
      if (!amt || amt <= 0) return toast.error("Ingresa un monto válido");
      if (amt > (user?.balance || 0)) return toast.error("Saldo insuficiente");
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/transfers", {
        ...form,
        recipient_rut: formatRut(form.recipient_rut),
        amount: Number(form.amount),
      });
      setReceipt(data);
      toast.success("Transferencia creada · esperando código del asesor");
      api.get("/me/transfers").then((r) => setHistory(r.data));
      setStep(4); // waiting-for-token
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 409: a pending transfer already exists — redirect user to it
      if (e?.response?.status === 409 && typeof detail === "object" && detail.code === "pending_transfer_exists") {
        toast.error(detail.message);
        try {
          const { data: pend } = await api.get(`/me/transfers/${detail.transfer_id}`);
          setReceipt(pend);
          setStep(4);
        } catch {}
        const { data: hist } = await api.get("/me/transfers");
        setHistory(hist);
      } else {
        toast.error(apiError(e, "Error al transferir"));
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmToken = async (token) => {
    if (!receipt) return;
    if (!/^\d{4}$/.test(token)) return toast.error("El código debe ser de 4 dígitos");
    setBusy(true);
    try {
      const { data } = await api.post(`/me/transfers/${receipt.id}/confirm`, { token });
      setReceipt(data);
      toast.success("Transferencia completada");
      refreshMe();
      api.get("/me/transfers").then((r) => setHistory(r.data));
      setStep(5);
    } catch (e) {
      toast.error(apiError(e, "Código inválido"));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setForm({ bank: "Banco de Chile", account_type: "Cuenta Corriente", account_number: "", recipient_name: "", recipient_rut: "", recipient_email: "", amount: "", comment: "" });
    setReceipt(null);
    setStep(1);
  };

  return (
    <div className="space-y-8">
      <header>
        <div className="label-mono mb-2">Transferencias</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Envía dinero a cualquier banco en Chile</h1>
        <div className="mt-3 font-mono text-sm text-inkMuted">Saldo: <span className="text-ink">{formatCLP(user?.balance)}</span></div>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* wizard */}
        <div className="lg:col-span-2 bento p-8">
          <StepBar step={step} />
          <div className="mt-8">
            {step === 1 && (
              <div className="space-y-5">
                <h2 className="font-display text-2xl font-bold mb-4">Datos del destinatario</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Banco o institución">
                    <select data-testid="tr-bank" value={form.bank} onChange={(e) => set("bank", e.target.value)} className="input">
                      {BANKS.map((b) => <option key={b}>{b}</option>)}
                    </select>
                  </Field>
                  <Field label="Tipo de cuenta">
                    <select data-testid="tr-account-type" value={form.account_type} onChange={(e) => set("account_type", e.target.value)} className="input">
                      {["Cuenta Corriente", "Cuenta Vista", "Cuenta RUT", "Cuenta Ahorro", "Cuenta Electrónica"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Número de cuenta">
                  <input data-testid="tr-account-number" value={form.account_number} onChange={(e) => set("account_number", e.target.value.replace(/\D/g, ""))} className="input font-mono" placeholder="0000000000" />
                </Field>
                <Field label="Nombre del destinatario">
                  <input data-testid="tr-recipient-name" value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} className="input" placeholder="Ej: Rodrigo Vergara" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="RUT destinatario">
                    <input data-testid="tr-recipient-rut" value={form.recipient_rut} onChange={(e) => set("recipient_rut", formatRut(e.target.value))} className="input font-mono" placeholder="12.345.678-9" />
                  </Field>
                  <Field label="Email destinatario">
                    <input data-testid="tr-recipient-email" type="email" value={form.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} className="input" placeholder="correo@ejemplo.cl" />
                  </Field>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <h2 className="font-display text-2xl font-bold mb-4">Monto y motivo</h2>
                <Field label="Monto (CLP)">
                  <input data-testid="tr-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="input font-mono text-3xl h-20" placeholder="0" />
                </Field>
                <Field label="Comentario (opcional)">
                  <input data-testid="tr-comment" value={form.comment} onChange={(e) => set("comment", e.target.value)} className="input" placeholder="Ej: Arriendo abril" />
                </Field>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="font-display text-2xl font-bold mb-6">Confirma los datos</h2>
                <dl className="divide-y divide-hairline border border-hairline">
                  <Row k="Destinatario" v={form.recipient_name} />
                  <Row k="RUT" v={form.recipient_rut} mono />
                  <Row k="Banco" v={form.bank} />
                  <Row k="Tipo de cuenta" v={form.account_type} />
                  <Row k="N° de cuenta" v={form.account_number} mono />
                  <Row k="Email" v={form.recipient_email} />
                  <Row k="Comentario" v={form.comment || "—"} />
                  <Row k="Monto" v={formatCLP(form.amount)} mono highlight />
                </dl>
              </div>
            )}

            {step === 4 && receipt && <TokenWaiting data={receipt} onConfirm={confirmToken} busy={busy} />}

            {step === 5 && receipt && <TransferReceipt data={receipt} onReset={reset} />}

            {step < 4 && (
              <div className="flex items-center justify-between mt-8">
                <button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="h-11 px-4 border border-hairline text-sm disabled:opacity-40 flex items-center gap-2">
                  <ArrowLeft size={14} /> Atrás
                </button>
                {step < 3 ? (
                  <button data-testid="tr-next" onClick={goNext} className="h-11 px-5 bg-forest text-bg text-sm font-semibold flex items-center gap-2 hover:bg-forest-hover">
                    Continuar <ArrowRight size={14} weight="bold" />
                  </button>
                ) : (
                  <button data-testid="tr-confirm" onClick={submit} disabled={busy} className="h-11 px-5 bg-burnt text-bg text-sm font-semibold flex items-center gap-2 hover:bg-burnt-hover disabled:opacity-60">
                    {busy ? "Enviando…" : "Confirmar y transferir"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* history */}
        <div className="bento">
          <div className="px-6 py-4 border-b border-hairline">
            <h3 className="font-display text-lg font-bold">Historial</h3>
          </div>
          <div className="max-h-[520px] overflow-auto">
            {history.length === 0 && <div className="p-6 text-sm text-inkMuted">Sin transferencias.</div>}
            {history.map((h) => {
              const isPending = h.status === "pending_token";
              const isCancelled = h.status === "cancelado";
              const isBlocked = h.status === "rechazado_token";
              const resume = () => { setReceipt(h); setStep(4); };
              return (
                <div key={h.id} className="px-6 py-4 border-b border-hairline hover:bg-bg">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm truncate">{h.recipient_name}</div>
                    <div className={`font-mono text-sm flex-shrink-0 ${isPending || isCancelled || isBlocked ? "text-inkMuted" : "text-burnt"}`}>{isPending || isCancelled || isBlocked ? "" : "− "}{formatCLP(h.amount)}</div>
                  </div>
                  <div className="label-mono mt-1 flex items-center gap-2 flex-wrap">
                    <span>{h.bank} · {formatDateTime(h.created_at)}</span>
                    {isPending && (
                      <button onClick={resume} className="px-1.5 py-0.5 bg-burnt/15 text-burnt hover:bg-burnt hover:text-bg transition-colors text-[10px]">
                        Pendiente · ingresar código
                      </button>
                    )}
                    {isCancelled && <span className="px-1.5 py-0.5 bg-danger/15 text-danger text-[10px]">Cancelada</span>}
                    {isBlocked && <span className="px-1.5 py-0.5 bg-danger/15 text-danger text-[10px]">Bloqueada</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .input { width: 100%; height: 52px; padding: 0 16px; border: 1px solid rgba(13,31,22,0.2); background: #fff; font-size: 15px; }
        .input:focus { outline: none; border-color: #0A2540; box-shadow: inset 0 0 0 1px #0A2540; }
      `}</style>
    </div>
  );
}

function StepBar({ step }) {
  const steps = ["Destinatario", "Monto", "Confirmar", "Código", "Comprobante"];
  return (
    <div className="grid grid-cols-5 gap-0 border border-hairline">
      {steps.map((s, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <div key={s} className={`px-3 py-3 ${i !== 4 ? "border-r border-hairline" : ""} ${active ? "bg-forest text-bg" : done ? "bg-forest/10 text-forest" : "bg-bg text-inkMuted"}`}>
            <div className="label-mono" style={{ color: active ? "rgba(247,246,242,0.7)" : undefined }}>Paso {n}</div>
            <div className="text-sm font-semibold">{s}</div>
          </div>
        );
      })}
    </div>
  );
}

function TokenWaiting({ data, onConfirm, busy }) {
  const [token, setToken] = useState("");
  return (
    <div data-testid="token-waiting" className="text-center">
      <div className="inline-flex items-center gap-2 px-3 py-1 bg-burnt/10 text-burnt font-mono text-xs uppercase tracking-widest mb-4">
        Pendiente de código
      </div>
      <h2 className="font-display text-3xl font-black tracking-tighter">Esperando código del asesor</h2>
      <p className="text-sm text-inkMuted mt-3 max-w-md mx-auto">
        Tu transferencia de <span className="font-mono text-ink">{formatCLP(data.amount)}</span> a <span className="font-semibold">{data.recipient_name}</span> está pendiente.
        Tu asesor te enviará un código de 4 dígitos para autorizarla.
      </p>
      <div className="my-8 max-w-xs mx-auto">
        <input
          data-testid="token-input"
          type="text"
          inputMode="numeric"
          maxLength={4}
          value={token}
          onChange={(e) => setToken(e.target.value.replace(/\D/g, "").slice(0, 4))}
          onKeyDown={(e) => { if (e.key === "Enter" && token.length === 4) onConfirm(token); }}
          autoFocus
          placeholder="0000"
          className="w-full h-20 border-2 border-hairline focus:border-forest bg-surface text-center font-mono text-5xl tracking-[0.5em] focus:outline-none"
        />
        <div className="mt-4 flex gap-3 justify-center">
          <button
            data-testid="token-confirm"
            onClick={() => onConfirm(token)}
            disabled={busy || token.length !== 4}
            className="h-11 px-6 bg-forest text-bg text-sm font-semibold hover:bg-forest-hover disabled:opacity-50"
          >
            {busy ? "Validando…" : "Confirmar transferencia"}
          </button>
        </div>
      </div>
      <div className="text-xs text-inkMuted">
        Si el código no funciona, contacta a tu asesor desde el chat. Tienes hasta 5 intentos.
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><label className="label-mono block mb-1.5">{label}</label>{children}</div>);
}
function Row({ k, v, mono, highlight }) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <div className="label-mono">{k}</div>
      <div className={`${mono ? "font-mono" : ""} ${highlight ? "text-2xl text-burnt font-semibold" : "text-sm"}`}>{v}</div>
    </div>
  );
}

function TransferReceipt({ data, onReset }) {
  return (
    <div data-testid="transfer-receipt" className="border-2 border-dashed border-ink p-8 bg-bg">
      <div className="flex items-center justify-center mb-4">
        <CheckCircle size={36} weight="duotone" className="text-success" />
      </div>
      <div className="text-center mb-6">
        <div className="label-mono">Comprobante de transferencia</div>
        <div className="font-display text-3xl font-black mt-1">¡Listo!</div>
        <div className="font-mono text-3xl text-forest mt-2">{formatCLP(data.amount)}</div>
      </div>
      <div className="h-[2px] receipt my-5" />
      <dl className="text-sm font-mono space-y-2">
        <RR k="Folio" v={data.id?.slice(0, 12).toUpperCase() || "—"} />
        <RR k="Fecha" v={formatDateTime(data.created_at)} />
        <RR k="Destinatario" v={data.recipient_name} />
        <RR k="RUT" v={data.recipient_rut} />
        <RR k="Banco" v={data.bank} />
        <RR k="Cuenta" v={`${data.account_type || ""} · ${data.account_number || ""}`} />
        <RR k="Estado" v={(data.status || "").toUpperCase()} />
      </dl>
      <div className="h-[2px] receipt my-5" />
      <div className="flex gap-3 justify-center mt-6">
        <button onClick={() => window.print()} className="h-11 px-5 border border-hairline text-sm flex items-center gap-2"><Receipt size={14} weight="bold" /> Imprimir</button>
        <button onClick={onReset} className="h-11 px-5 bg-forest text-bg text-sm font-semibold">Nueva transferencia</button>
      </div>
    </div>
  );
}
function RR({ k, v }) {
  return <div className="flex justify-between"><span className="text-inkMuted uppercase tracking-wider text-xs">{k}</span><span>{v}</span></div>;
}
